import { useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import {
  ArrowLeft,
  Target,
  Compass,
  Sparkles,
  ListOrdered,
  Hammer,
  RotateCcw,
} from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { useSurfaceTheme } from '../hooks/useSurfaceTheme';
import {
  SECTORES,
  TIPOS_SOLUCION,
  recomendarPlantillas,
  type PlantillaRecomendada,
} from '../lib/plantillas';
import { composicionApi, type ProcesoComp } from '../lib/composicion';
import { ApiError } from '../lib/api';

/**
 * Recorta el enunciado del problema a algo digno de título de proceso:
 * espacios colapsados, primera letra en mayúscula y corte en límite de
 * palabra (~60 caracteres) con puntos suspensivos.
 */
function tituloDesdeEnunciado(enunciado: string): string {
  const limpio = enunciado.trim().replace(/\s+/g, ' ').replace(/[.\s]+$/, '');
  const capitalizado = limpio.charAt(0).toUpperCase() + limpio.slice(1);
  const MAX = 60;
  if (capitalizado.length <= MAX) return capitalizado;
  const corte = capitalizado.slice(0, MAX);
  const ultimoEspacio = corte.lastIndexOf(' ');
  return `${corte.slice(0, ultimoEspacio > 30 ? ultimoEspacio : MAX)}…`;
}

/**
 * Entrada por problema (/nuevo) — «¿Qué querés resolver?».
 * Enunciado + sector; al enviar consulta GET /api/v1/plantillas/recomendar y
 * muestra la plantilla recomendada con su «porqué» explicado en clave XIA.
 */
export function NuevoPage() {
  useSurfaceTheme('bolsillo', 'light');
  const navigate = useNavigate();

  const [enunciado, setEnunciado] = useState('');
  const [sector, setSector] = useState('');
  const [tipoSolucion, setTipoSolucion] = useState('');
  const [tocado, setTocado] = useState(false);

  const mutation = useMutation<PlantillaRecomendada[], Error, void>({
    mutationFn: () => recomendarPlantillas(enunciado.trim(), sector, tipoSolucion || undefined),
  });

  /**
   * Remate del flujo: crea el proceso en 'borrador' y desemboca en el
   * compositor real. La página no crea todavía el problema en el backend
   * (eso llega con la entrada por problema persistida), así que no se
   * manda problema_id.
   */
  const crearProceso = useMutation<ProcesoComp, Error, { plantillaId: string | null }>({
    mutationFn: ({ plantillaId }) =>
      composicionApi.crearProceso({
        nombre: tituloDesdeEnunciado(enunciado),
        // Oleada 5: con plantilla el proceso nace COMPUESTO (fases, sesiones,
        // actividades y conexiones precableadas); «desde cero» = lienzo vacío.
        plantilla: plantillaId ?? undefined,
      }),
    onSuccess: (proceso) => navigate(`/compositor/${proceso.id}`),
  });

  const crearError = crearProceso.isError
    ? crearProceso.error instanceof ApiError
      ? crearProceso.error.detail
      : crearProceso.error.message
    : null;

  const enunciadoValido = enunciado.trim().length >= 8;

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setTocado(true);
    if (!enunciadoValido) return;
    mutation.mutate();
  }

  function reiniciar() {
    mutation.reset();
    setTocado(false);
  }

  const recomendadas = mutation.data ?? [];

  return (
    <div className="mx-auto flex min-h-screen max-w-pocket flex-col bg-surface-base">
      <header className="flex items-center gap-2 px-4 pb-2 pt-4">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex min-h-touch min-w-touch items-center justify-center rounded-md text-content-muted hover:bg-surface-sunken"
          aria-label="Volver"
        >
          <ArrowLeft className="h-5 w-5" aria-hidden="true" />
        </button>
        <img src="/brand/logopositivo.png" alt="ÁGORA" className="h-7 w-auto" />
      </header>

      <main className="flex-1 px-4 py-4">
        {recomendadas.length === 0 ? (
          <>
            <h1 className="font-display text-2xl font-bold text-content">
              ¿Qué querés resolver?
            </h1>
            <p className="mt-1 font-sans text-sm text-content-muted">
              Describí el problema de tu comunidad. ÁGORA te sugiere con qué
              método arrancar el proceso participativo.
            </p>

            <form onSubmit={onSubmit} className="mt-6 flex flex-col gap-4" noValidate>
              <div className="flex flex-col gap-1">
                <label
                  htmlFor="enunciado"
                  className="font-sans text-sm font-medium text-content"
                >
                  El problema, en tus palabras
                  <span className="text-error" aria-hidden="true">
                    {' '}*
                  </span>
                </label>
                <textarea
                  id="enunciado"
                  value={enunciado}
                  onChange={(e) => setEnunciado(e.target.value)}
                  rows={4}
                  placeholder="Ej.: En la zona 3 el agua potable llega solo tres días por semana y afecta a más de 200 familias."
                  aria-invalid={tocado && !enunciadoValido ? true : undefined}
                  aria-describedby={
                    tocado && !enunciadoValido ? 'enunciado-error' : undefined
                  }
                  className={`min-h-touch w-full rounded-md border bg-surface-raised px-3 py-2 text-base text-content placeholder:text-content-muted transition-colors duration-fast focus-visible:outline-none focus-visible:shadow-focus ${
                    tocado && !enunciadoValido ? 'border-error' : 'border-border-strong'
                  }`}
                />
                {tocado && !enunciadoValido && (
                  <p id="enunciado-error" role="alert" className="text-sm text-error">
                    Contanos un poco más: al menos una frase que describa el
                    problema.
                  </p>
                )}
              </div>

              {/* Dos dimensiones ortogonales: el DOMINIO del problema y el
                  TIPO de solución que se busca — «software del sector
                  Justicia» es sector=justicia + tipo=digital. */}
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="flex flex-col gap-1">
                  <label
                    htmlFor="sector"
                    className="font-sans text-sm font-medium text-content"
                  >
                    Sector
                  </label>
                  <select
                    id="sector"
                    value={sector}
                    onChange={(e) => setSector(e.target.value)}
                    className="min-h-touch w-full rounded-md border border-border-strong bg-surface-raised px-3 text-base text-content transition-colors duration-fast focus-visible:outline-none focus-visible:shadow-focus"
                  >
                    <option value="">Elegí un sector (opcional)</option>
                    {SECTORES.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-col gap-1">
                  <label
                    htmlFor="tipo-solucion"
                    className="font-sans text-sm font-medium text-content"
                  >
                    Tipo de solución
                  </label>
                  <select
                    id="tipo-solucion"
                    value={tipoSolucion}
                    onChange={(e) => setTipoSolucion(e.target.value)}
                    className="min-h-touch w-full rounded-md border border-border-strong bg-surface-raised px-3 text-base text-content transition-colors duration-fast focus-visible:outline-none focus-visible:shadow-focus"
                  >
                    <option value="">¿Qué buscás crear? (opcional)</option>
                    {TIPOS_SOLUCION.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {mutation.isError && (
                <p role="alert" className="text-sm text-error">
                  {mutation.error.message}
                </p>
              )}

              <Button
                type="submit"
                variant="primary"
                fullWidth
                loading={mutation.isPending}
                leadingIcon={<Compass className="h-5 w-5" aria-hidden="true" />}
              >
                Ver método sugerido
              </Button>
            </form>

            <p className="mt-4 flex items-start gap-2 font-sans text-xs text-content-muted">
              <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-violet" aria-hidden="true" />
              La recomendación es un punto de partida. El facilitador siempre
              puede ajustar el método.
            </p>
          </>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <h1 className="font-display text-xl font-bold text-content">
                Método sugerido
              </h1>
              <Button
                variant="ghost"
                size="sm"
                onClick={reiniciar}
                leadingIcon={<RotateCcw className="h-4 w-4" aria-hidden="true" />}
              >
                Cambiar
              </Button>
            </div>
            <p className="mt-1 font-sans text-sm text-content-muted">
              Para «{enunciado.trim()}»
            </p>

            <div className="mt-5 flex flex-col gap-4">
              {recomendadas.map((plantilla, i) => (
                <Card
                  key={plantilla.id}
                  title={plantilla.nombre}
                  footer={<Badge variant="neutral">{plantilla.etiqueta}</Badge>}
                  className={i === 0 ? 'border-primary' : undefined}
                >
                  {i === 0 && (
                    <div className="mb-3">
                      <Badge variant="primary">Recomendada</Badge>
                    </div>
                  )}

                  {/* El «porqué» en clave XIA */}
                  <div className="flex items-start gap-2">
                    <Target
                      className="mt-0.5 h-4 w-4 shrink-0 text-primary"
                      aria-hidden="true"
                    />
                    <div>
                      <p className="font-sans text-xs font-semibold uppercase tracking-wide text-content-muted">
                        Por qué este método
                      </p>
                      <p className="font-sans text-sm text-content">{plantilla.porque}</p>
                    </div>
                  </div>

                  {/* Recorrido de fases */}
                  <div className="mt-3 flex items-start gap-2">
                    <ListOrdered
                      className="mt-0.5 h-4 w-4 shrink-0 text-cyan"
                      aria-hidden="true"
                    />
                    <div>
                      <p className="font-sans text-xs font-semibold uppercase tracking-wide text-content-muted">
                        Cómo avanza
                      </p>
                      <ol className="mt-1 flex flex-col gap-1">
                        {plantilla.fases.map((fase, j) => (
                          <li
                            key={fase}
                            className="flex items-center gap-2 font-sans text-sm text-content"
                          >
                            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-surface-sunken font-sans text-xs font-semibold text-content-muted">
                              {j + 1}
                            </span>
                            {fase}
                          </li>
                        ))}
                      </ol>
                    </div>
                  </div>

                  {/* TODO M3-E2: aquí se instanciará plantilla.manifest (fases,
                      sesiones y actividades de la plantilla). Por ahora el botón
                      crea el proceso VACÍO, igual que «Empezar desde cero». */}
                  <Button
                    variant={i === 0 ? 'primary' : 'secondary'}
                    fullWidth
                    className="mt-4"
                    loading={
                      crearProceso.isPending &&
                      crearProceso.variables?.plantillaId === plantilla.id
                    }
                    disabled={crearProceso.isPending}
                    onClick={() => crearProceso.mutate({ plantillaId: plantilla.id })}
                    leadingIcon={<Hammer className="h-4 w-4" aria-hidden="true" />}
                  >
                    Componer este proceso
                  </Button>
                </Card>
              ))}

              <Card title="Empezar desde cero">
                <p className="font-sans text-sm text-content-muted">
                  Si preferís armar el proceso a tu manera, arrancá con el lienzo
                  vacío y agregá vos las fases, sesiones y herramientas.
                </p>
                <Button
                  variant="secondary"
                  fullWidth
                  className="mt-4"
                  loading={
                    crearProceso.isPending && crearProceso.variables?.plantillaId === null
                  }
                  disabled={crearProceso.isPending}
                  onClick={() => crearProceso.mutate({ plantillaId: null })}
                  leadingIcon={<Hammer className="h-4 w-4" aria-hidden="true" />}
                >
                  Componer este proceso
                </Button>
              </Card>
            </div>

            {crearError && (
              <p role="alert" className="mt-4 text-sm text-error">
                {crearError}
              </p>
            )}

            <p className="mt-4 flex items-start gap-2 font-sans text-xs text-content-muted">
              <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-violet" aria-hidden="true" />
              {/* TODO F0-P5: reemplazar el fijo por la recomendación del driver /ai. */}
              Sugerencia preliminar basada en 2 plantillas base. La lectura por IA
              del enunciado se activa en una fase posterior.
            </p>
          </>
        )}
      </main>
    </div>
  );
}
